import * as ko from "knockout";
import { ToastNotification, ToastNotificationKind } from "./toastnotification";
import { ResponseContainer } from "../Models/responsecontainer";
import { DialogViewModel, DialogAction } from "./dialogviewmodel";
import { registerDialog } from "dialog-polyfill";
import "dialog-polyfill/dialog-polyfill.css";
import "qtip2";
import "qtip2/dist/jquery.qtip.min.css";
import "../Templates/session-expiry-dialog-content.html";
import { UserSettings } from "../Models/usersettings";
import { AjaxAuthHeader } from "../Models/ajaxAuthHeader";
import { LoginResponse } from "../Models/loginresponse";
import { KnockoutExtensions } from "../KnockoutExtensions/knockoutextensions";
import { UserSettingsUpdate } from "../Models/profileupdate";

// Enables Hot Module Replacement.
// tslint:disable-next-line
declare var module: any;
if (module.hot) {
    module.hot.accept();
}

class ViewModelBase {

    private static _idCharacters = "_0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz".split("");

    // State properties
    protected isDirtyFlag: DirtyFlag;
    public isDirtyOverride = ko.observable<boolean>(false);
    public isBusy = ko.observable<boolean>(false);
    public isSessionExpired = ko.observable<boolean>(false);
    public busyText = ko.observable<string>(null);
    public isInitialising = ko.observable<boolean>(true);
    public allowBack = ko.observable<boolean>(true);
    public allowUnload = ko.observable<boolean>(true);
    public settings: UserSettings = null;
    public isDirty = ko.computed(() => this.isDirtyOverride() ? this.isDirtyOverride() : this.isDirtyFlag ? this.isDirtyFlag.isDirty() : false);
    public hasExpiredCard = ko.computed(() => (this.readSetting("card-expired") == "1" ? true : false));
    public userRegion = ko.computed(() => this.readSetting("region"));
    public hasChatEnabled: boolean = true;
    protected settingKeyPrefix: string = null;
    protected ignorePopState: boolean = false;
    protected isBackButtonInterceptionEnabled: boolean = false;
    protected ignoreDataChanges: boolean = false;
    protected isSavingSettings: boolean = false;

    constructor() {
        // Automatically tracks dirty state using the toJs method
        this.isDirtyFlag = DirtyFlag.createInstance(this, false);
        // Store the settings for use else where
        this.settings = JSON.parse(this.readSetting("settings"));

        // tslint:disable-next-line
        if (typeof (window as any).showValidationError === "undefined") {
            // tslint:disable-next-line
            (window as any).showValidationError = showValidationError;
        }
    }

    // Serialisation

    public resetIsDirty(): void {
        this.isDirtyOverride(null);
        this.isDirtyFlag.reset();
    }

    public setIsDirty(value: boolean): void {
        this.isDirtyOverride(null);
        this.isDirtyFlag = DirtyFlag.createInstance(this, value);
    }

    /**
     * Default included and ignored properties for json serialisation.
     */
    protected getPersistenceIncludedProperties(): string[] {
        return [];
    }

    protected getPersistenceIgnoredProperties(): string[] {
        return ["isDirtyFlag", "isDirty", "isDirtyOverride", "isBusy", "state", "parent", "busyText",
            "pageViewModel", "allowBack", "allowUnload", "isInitialising", "settingKeyPrefix", "ignorePopState",
            "isBackButtonInterceptionEnabled"];
    }

    /**
     * Override in derived types to specify mapping configuration
     */
    public getLoadMappingOptions(): KnockoutMappingOptions {
        return {
            include: this.getPersistenceIncludedProperties(),
            ignore: this.getPersistenceIgnoredProperties()
        };
    }

    /**
     * Override in derived types to specify mapping configuration
     */
    public getSaveMappingOptions(): KnockoutMappingOptions {
        return {
            include: this.getPersistenceIncludedProperties(),
            ignore: this.getPersistenceIgnoredProperties()
        };
    }

    /**
     * Returns a model object from this viewModel, using the knockout mapping options.
     */
    // tslint:disable-next-line
    public toJs(): any {
        return ko.mapping.toJS(this, this.getSaveMappingOptions());
    }

    /**
     * Returns a JSON string from the model object returned by toJs().
     */
    public toJson(): string {
        return JSON.stringify(this.toJs());
    }

    public toPrettyJson(): string {
        return JSON.stringify(this.toJs(), null, 4);
    }

    /**
     * Populates viewmodel properties from the passed model data.
     */
    public fromJs<T>(data: T): void {
        ko.mapping.fromJS(data, this.getLoadMappingOptions(), this);
    }


    // Actions

    public saveRequest(): void {
        const validationErrors = this.validate();
        if (validationErrors == null) {
            if (this.isDirty()) {
                this.save();
            }
        } else {
            var validationErrorMessage = `Please correct these validation error(s) before saving:<br/><br/>${validationErrors}`;
            this.showNotification(new ToastNotification(ToastNotificationKind.Error, "Validation Error", validationErrorMessage));
        }
    }

    /**
     * A request from the user to navigate back.
     */
    public backRequest(): void {
        if (this.ignoreDataChanges) {
            this.back();
            return;
        }
        if (this.isDirty()) {
            this.promptToSaveChanges(() => {
                this.save(() => this.back());
                return false;
            }, () => {
                this.back();
                return false;
            });
        } else {
            this.back();
        }
    }

    public back(): void {
        this.allowBack(true);
        this.allowUnload(true);
        history.back();
    }

    public redirectTo(action: string): void {
        window.open(action, "_blank");
    }

    public bannerRequest(action: string): void {
        this.removeSetting("card-expired");
        this.navigate(action);
        $('.page-banner-extended').fadeOut();
    }

    public bannerClose(): void {
        this.removeSetting("card-expired");
        $('.page-banner-extended').fadeOut();
    }

    // Fetch / post remote data

    /**
     * Fetch json data
     */
    // tslint:disable-next-line
    protected getJsonData<T>(url: string, success: (data: T) => void, error?: JQuery.Ajax.ErrorCallback<any>, complete?: JQuery.Ajax.CompleteCallback<any>) {
        var call = $.getJSON(url, (response: ResponseContainer) => {
            if (success != null) {
                success(response.value as T);
            }
        });
        if (error) {
            call.fail(error);
        }
        if (complete) {
            call.always(complete);
        }
    }

    /**
    * POST data - include Basiv Auth header
    */
    // tslint:disable-next-line
    public postJsonData<T>(url: string, data: T, success?: JQuery.Ajax.SuccessCallback<any>, error?: JQuery.Ajax.ErrorCallback<any>, complete?: JQuery.Ajax.CompleteCallback<any>, authInfo?: string): void {
        this.ajax<T>(url, "POST", data, (authInfo != null ? authInfo : null), (response: ResponseContainer, textStatus, jqXhr) => {
            if (success != null) {
                success(response.value as T, textStatus, jqXhr);
            }
        }, error, complete);
    }

    /**
    * POST data - include Basiv Auth header
    */
    // tslint:disable-next-line
    public postJsonFileData<T>(url: string, data: T, success?: JQuery.Ajax.SuccessCallback<any>, error?: JQuery.Ajax.ErrorCallback<any>, complete?: JQuery.Ajax.CompleteCallback<any>, authInfo?: string): void {
        this.ajax(url, "POST", data, (authInfo != null ? authInfo : null), (response: Blob, textStatus, jqXhr) => {
            if (success != null) {
                success(response, textStatus, jqXhr);
            }
        }, error, complete);
    }

    /**
     * PUT data
     */
    // tslint:disable-next-line
    public putJsonData<T>(url: string, data: T, success?: JQuery.Ajax.SuccessCallback<any>, error?: JQuery.Ajax.ErrorCallback<any>, complete?: JQuery.Ajax.CompleteCallback<any>): void {
        this.ajax<T>(url, "PUT", data, null, (response: ResponseContainer, textStatus, jqXhr) => {
            if (success != null) {
                success(response.value as T, textStatus, jqXhr);
            }
        }, error, complete);
    }

    // tslint:disable-next-line
    public deleteData(url: string, success?: JQuery.Ajax.SuccessCallback<any>, error?: JQuery.Ajax.ErrorCallback<any>, complete?: JQuery.Ajax.CompleteCallback<any>): void {
        this.ajax(url, "DELETE", null, null, success, error, complete);
    }

    // tslint:disable-next-line
    private ajax<T>(url: string,
        method: string,
        data: T,
        authHeader?: string,
        // tslint:disable-next-line
        success?: JQuery.Ajax.SuccessCallback<any>,
        // tslint:disable-next-line
        error?: JQuery.Ajax.ErrorCallback<any>,
        // tslint:disable-next-line
        complete?: JQuery.Ajax.CompleteCallback<any>): void {

        var settings: JQueryAjaxSettings = {
            type: method,
            url: url,
            contentType: data != null ? "application/json; charset=utf-8" : null,
            data: data != null ? JSON.stringify(data) : null,
            dataType: data != null ? "json" : null,
            error: error,
            success: success,
            complete: complete
        };

        if (authHeader != null) {
            settings = {
                type: method,
                url: url,
                contentType: data != null ? "application/json; charset=utf-8" : null,
                data: data != null ? JSON.stringify(data) : null,
                dataType: data != null ? "json" : null,
                headers: {
                    "Authorization": authHeader,
                    "Access-Control-Allow-Origin": "*"
                },
                error: error,
                success: success,
                complete: complete
            };
        }

        jQuery.ajax(settings);
    }


    // Methods

    /**
     * Validates the ViewModel and returns true if it is found to be valid. Will show any validation error messages in the bound UI.
     */
    public validate(): string[] {
        var errors = ko.validation.group(this.getValidationObject(), { deep: true });
        if (errors().length > 0) {
            errors.showAllMessages(true);
            return errors();
        } else {
            return null;
        }
    }

    /**
     * Returns an object containing all of the properties that should be validated before a save operation
     */
    // tslint:disable-next-line
    public getValidationObject(): any {
        const obj = {};
        $.each(this.getSaveMappingOptions().include,
            (index: number, memberName: string) => {
                // tslint:disable-next-line
                (obj as any)[memberName] = (this as any)[memberName];
            });
        return obj;
    }

    /**
     * Creates a dialog, binds it to the passed DialogViewModel and shows it.
     */
    public showDialog(dialogViewModel: DialogViewModel) {
        // Create the dialog
        const dialogId = `dialog-${dialogViewModel.id}`;
        $('#' + dialogId).remove();
        var $dialog = $("<dialog/>").attr("id", dialogId).attr("data-bind", "mdlDialog");
        $("body").append($dialog);
        // Bind to the viewmodel
        var dialogElement = $dialog.get(0);
        ko.applyBindings(dialogViewModel, dialogElement);
        // Show
        // tslint:disable-next-line
        registerDialog(dialogElement);
        if (dialogViewModel.onBeforeShowModal != null) {
            dialogViewModel.onBeforeShowModal(dialogElement);
        }
        // tslint:disable-next-line
        ($dialog.get(0) as any).showModal();
    }

    /**
     * Creates a dialog, binds it to the passed DialogViewModel and shows it.
     */
    public showDialogWithCallback(dialogViewModel: DialogViewModel, callback: () => void) {
        // Create the dialog
        const dialogId = `dialog-${dialogViewModel.id}`;
        $('#' + dialogId).remove();
        var $dialog = $("<dialog/>").attr("id", dialogId).attr("data-bind", "mdlDialog");
        $("body").append($dialog);
        // Bind to the viewmodel
        var dialogElement = $dialog.get(0);
        ko.applyBindings(dialogViewModel, dialogElement);
        // Show
        // tslint:disable-next-line
        registerDialog(dialogElement);
        if (dialogViewModel.onBeforeShowModal != null) {
            dialogViewModel.onBeforeShowModal(dialogElement);
        }
        // tslint:disable-next-line
        ($dialog.get(0) as any).showModal();

        if (callback !== undefined) {
            setTimeout(() => {
                callback();
            }, 200);
        }

    }

    /**
     * Puts a handler in place that prevents use of the back button if this viewmodel is dirty, instead
     * calling the backRequest() method.
     */
    protected interceptBackButton(): void {
        this.allowBack(false);
        // History API 
        if (window.history && window.history.pushState) {
            history.pushState("nohb", null);
            if (navigator.userAgent.indexOf("Safari") === -1) {
                this.hookPopState();
            } else {
                // Safari specific - we must ignore the popstate that comes in right after page load
                if (document.readyState === "complete") {
                    // Document fully loaded, add event listener immediately

                    // Add event handler in a timer to avoid Safari popstate on page load
                    setTimeout(() => {
                        this.hookPopState();
                    }, 1000);
                } else {
                    // Document not fully loaded, add popstate listener on page load

                    // tslint:disable-next-line
                    $(window as any).on("load",
                        () => {
                            // Add event handler in a timer to avoid Safari popstate on page load
                            setTimeout(() => {
                                this.hookPopState();
                            }, 1000);
                        });
                }
            }
            this.isBackButtonInterceptionEnabled = true;
        }
    }

    private hookPopState() {
        // tslint:disable-next-line
        $(window as any).on("popstate",
            (event) => {
                this.onPopStateEvent(event);
            });
    }

    // tslint:disable-next-line
    private onPopStateEvent(event: any) {
        // tslint:disable-next-line
        console.log("popstate " + (event as any).state);
        if (this.ignorePopState) {
            console.log("Ignoring popstate");
            this.ignorePopState = false;
            return;
        }
        // tslint:disable-next-line
        if (!this.allowBack() && !(event.originalEvent as any).state) {
            if (this.isDirty()) {
                history.pushState("nohb", null);
                this.backRequest();
                return;
            } else {
                this.backRequest();
                return;
            }
        }
        history.back();
    }

    protected replaceHistory(url: string): void {
        if (this.isBackButtonInterceptionEnabled) {
            this.ignorePopState = true;
            // tslint:disable-next-line
            $(window as any).on("popstate.replace",
                (event) => {
                    history.replaceState(null, null, url);
                    history.pushState("nohb", null, "");
                });
            history.back();
        } else {
            history.replaceState(null, null, url);
        }
    }

    protected interceptSaveHotkey(): void {
        // tslint:disable-next-line
        $(document as any).on("keydown", (e) => {
            if (e.ctrlKey && e.which === 83) { // Check for the Ctrl key being pressed, and if the key = [S] (83)
                e.preventDefault();
                this.saveRequest();
            }
        });
    }

    protected warnOnUnload(): void {
        this.allowUnload(false);
        // tslint:disable-next-line
        $(window as any).on("beforeunload",
            (e: BeforeUnloadEvent) => {
                if (!this.allowUnload() && this.isDirty() && this.readSetting("isLoggedIn") !== null && this.readSetting("isLoggedIn") == "1") {
                    e.returnValue = "You have unsaved changes. Are you sure you want to leave this site?";
                    return "You have unsaved changes. Are you sure you want to leave this site?";
                }
            });
    }

    public showNotification(notification: ToastNotification, showClose: boolean = false): void {
        if (!showClose)
            notification.show();
        else
            notification.showWithClose();
    }

    /**
     * Body is initially hidden when the main viewmodel is loading. This will show it.
     */
    protected showBody(): void {
        setTimeout(() => {
            $("body").show();
        }, 1);
    }

    /**
     * Body is initially hidden when the main viewmodel is loading. This will show it.
     */
    protected showBodyWhenPageIsFullyLoaded(afterShowBody: () => void = null): void {
        if (document.readyState === "complete") {
            this.onPageIsFullyLoaded();
        } else {
            $(window).on("load", () => {
                this.onPageIsFullyLoaded(afterShowBody);
            });
        }
    }

    private onPageIsFullyLoaded(afterShowBody: () => void = null) {
        $("body").show();
        if (afterShowBody != null) {
            afterShowBody();
        }
    }

    /**
     * Navigate to the site home page.
     */
    public navigateHome(): void {
        location.href = "/";
    }

    /**
     * Request to navigate to a url. Detects pending changes and shows prompt to save / ignore.
     */
    public navigateRequest(url: string): void {
        if (this.isDirty()) {
            this.promptToSaveChanges(() => {
                this.save(() => {
                    this.navigate(url);
                });
                return false;
            }, () => {
                this.navigate(url);
                return false;
            });
        } else {
            // Not dirty, immediately navigate
            this.navigate(url);
        }
    }

    /**
 * Request to navigate to a url. Detects pending changes and shows prompt to save / ignore.
 */
    public navigateRequestBlankTarget(url: string): void {
        window.open(url, "_blank");
    }

    /**
     * Immediately navigate to the passed url.
     */
    public navigate(url: string): void {
        location.href = url;
    }

    /**
     * Shows as "Save Changes?" prompt providing the option to save, leave anyway or cancel.
     */
    public promptToSaveChanges(saveAction: () => boolean, leaveAction: () => boolean): void {
        // Prompt to save changes
        const dialogViewModel = new DialogViewModel("unsavedchanges",
            "Unsaved Changes",
            "You currently have unsaved changes. Do you want to save your changes before leaving?",
            [
                new DialogAction("Save Changes",
                    saveAction,
                    true),
                new DialogAction("Leave without Saving",
                    leaveAction,
                    true),
                new DialogAction("Cancel", () => false, true)
            ]);
        this.showDialog(dialogViewModel);
    }

    /**
     * Immediately save any pending changes.
     */
    public save(afterSave: () => void = null): void {
        // No built in implementation
    }

    /**
     * Returns a unique id, randomnly generated and checked for uniqueness in the DOM
     */
    public getUniqueId(): string {
        const idlength = 10;
        var getId = () => {
            var idValue = "";
            for (var i = 0; i < idlength; i++) {
                idValue += ViewModelBase._idCharacters[Math.floor(Math.random() * ViewModelBase._idCharacters.length)];
            }
            return idValue;
        };
        var id = getId();
        while ($("#" + id).length > 0) {
            id = getId();
        }
        return id;
    }

    public onCopyToClipboardSuccess(): void {
        this.showNotification(new ToastNotification(ToastNotificationKind.Success, "Clipboard", "Content copied to the clipboard OK"));
    }

    public onCopyToClipboardError(): void {
        this.showNotification(new ToastNotification(ToastNotificationKind.Error, "Clipboard", "Could not copy content to the clipboard. Please highlight the content and press Ctrl+C to copy to the clipboard."));
    }

    public writeSetting(key: string, value: string): void {
        if (this.settingKeyPrefix != null) {
            key = this.settingKeyPrefix + key;
        }
        if (value == null || undefined == value) {
            window.localStorage.removeItem(key);
        }
        else {
            window.localStorage.setItem(key, value);
        }
    }

    public readSetting(key: string): string {
        if (this.settingKeyPrefix != null) {
            key = this.settingKeyPrefix + key;
        }
        return window.localStorage.getItem(key);
    }

    public removeSetting(key: string) {
        if (this.settingKeyPrefix != null) {
            key = this.settingKeyPrefix + key;
        }
        window.localStorage.removeItem(key);
    }

    protected onActiveViewChanged(name: string, isMainView: boolean = false) {
        if (isMainView) {
            this.removeHashFromLocation();
        } else {
            this.setLocationHash(`#${name}`);
        }
    }

    protected setLocationHash(hash: string) {
        history.replaceState(null, null, hash);
    }

    protected removeHashFromLocation(): void {
        history.replaceState(null, null, window.location.pathname
            + window.location.search);
    }

    protected initialiseActiveView(): void {
        if (location.hash !== "") {
            var $tab = $(`${location.hash}-tab`);
            if ($tab.length > 0) {
                if (document.readyState === "complete") {
                    this.setTabSelected($tab);
                } else {
                    $(window).on("load",
                        () => {
                            this.setTabSelected($tab);
                        });
                }
            }
        }
    }

    protected setActiveTab(name: string) {
        // tslint:disable-next-line
        this.setTabSelected($(`#${name}-tab`));
    }

    private setTabSelected($tab: JQuery) {
        // tslint:disable-next-line
        ($tab.get(0) as any).click();
    }

    protected focusInput(divSelector: string = ".initial-focus") {
        setTimeout(() => {
            $(`${divSelector} input`).focus();
            $(divSelector).addClass("is-focused");
        }, 1);
    }

    public getRecentRecipesViewModel(): RecentItemsViewModel {
        return RecentItemsContainer.getRecentRecipes();
    }

    public getRecentIngredientsViewModel(): RecentItemsViewModel {
        return RecentItemsContainer.getRecentIngredients();
    }

    public completeTour() {
        this.putJsonData("/api/account/completeTour", null);
    }

    public saveSettings() {

        if (this.isSavingSettings)
            return;

        // Save the data to the database as well
        var userSettings: UserSettingsUpdate = new UserSettingsUpdate();
        if (this.readSetting("lastNotificationId") != null) {
            userSettings.lastNotificationId = parseInt(this.readSetting("lastNotificationId"));
        }
        userSettings.recentIngredients = this.readSetting("recent-ingredient");
        userSettings.recentRecipes = this.readSetting("recent-recipe");
        if (this.readSetting("resultListViewStyle") != null) {
            userSettings.resultListViewStyle = parseInt(this.readSetting("resultListViewStyle"));
        }
        if (this.readSetting("reportListViewStyle") != null) {
            userSettings.reportsListViewStyle = parseInt(this.readSetting("reportListViewStyle"));
        }
        userSettings.copyToClipboardExcelFormat = this.readSetting("copy-to-clipboard-excel");
        userSettings.searchOptions = this.readSetting("searchOptions");

        this.isSavingSettings = true;
        this.postJsonData("/api/account/savesettings", userSettings,
            () => {
                this.isSavingSettings = false;
            },
            (err) => {
                console.log(`Failed to save local user settings. ${err.statusText}`);
                this.isSavingSettings = false;
            }
        );
    }

    public getBroswer(): string {
        //Check if browser is IE 10 or older
        if (navigator.userAgent.search("MSIE") >= 0) {
            return "ie";
        }
        //Check if browser is IE 10 or older
        if (navigator.userAgent.search("Trident/") >= 0) {
            return "ie";
        }
        //Check if browser is Edge
        if (navigator.userAgent.search("Edge") >= 0) {
            return "edge";
        }
        //Check if browser is Chrome
        else if (navigator.userAgent.search("Chrome") >= 0) {
            return "chrome";
        }
        //Check if browser is Firefox 
        else if (navigator.userAgent.search("Firefox") >= 0) {
            return "firefox";
        }
        //Check if browser is Safari
        else if (navigator.userAgent.search("Safari") >= 0 && navigator.userAgent.search("Chrome") <= 0) {
            return "safari";
        }
        //Check if browser is Opera
        else if (navigator.userAgent.search("Opera") >= 0) {
            return "opera";
        }
    }

    public getBaseurl() {
        var _baseURL = location.protocol + "//" + location.hostname + (location.port && ":" + location.port) + "/";
        return _baseURL;
    }

    public roundNumber(value: number, decimalPlaces: number): number {
        if (typeof decimalPlaces === 'undefined' || +decimalPlaces === 0)
            return Math.round(value);

        value = +value;
        decimalPlaces = +decimalPlaces;

        if (isNaN(value) || !(typeof decimalPlaces === 'number' && decimalPlaces % 1 === 0))
            return NaN;

        // Shift
        var newValue = value.toString().split('e');
        value = Math.round(+(newValue[0] + 'e' + (newValue[1] ? (+newValue[1] + decimalPlaces) : decimalPlaces)));

        // Shift back
        newValue = value.toString().split('e');
        return +(newValue[0] + 'e' + (newValue[1] ? (+newValue[1] - decimalPlaces) : -decimalPlaces));
    }

    public padRight(size: number, str: string, padChar: string = " "): string {
        if (typeof str === 'undefined')
            return str;
        if (size - str.length + 1 <= 0) {
            return str;
        }
        return str + Array(size - str.length + 1).join(padChar);
    }

    public padLeft(size: number, str: string, padChar: string = " "): string {
        if (typeof str === 'undefined')
            return str;
        if (size - str.length + 1 <= 0) {
            return str;
        }
        return Array(size - str.length + 1).join(padChar) + str;
    }
    
    /**
     * Converts a string to its html characters completely.
     *
     * @param {String} str String with unescaped HTML characters
     **/
    public encodeHtml(str: string) {
        var buf = [];

        for (var i = str.length - 1; i >= 0; i--) {
            buf.unshift(['&#', str.charAt(i), ';'].join(''));
        }

        return buf.join('');
    }

    /**
     * Converts an html characterSet into its original character.
     *
     * @param {String} str htmlSet entities
     **/
    public decodeHtml(str: string) {
        const span = document.createElement('span');

        return str.replace(/&[#A-Za-z0-9]+;/gi, (entity, position, str) => {
            span.innerHTML = entity;
            return span.innerText;
        });
    }

    public sessionExpired() {
        var username = this.readSetting("nutricalc-login");
        if (!this.isSessionExpired()) {
            this.isSessionExpired(true);
            this.expireSession();
            $('.sessionexpired-overlay').show();
            var dialog = new SessionexpiryDialogViewModel(this);
            this.showDialog(dialog);
            setTimeout(() => dialog.setFocus("#password"), 400);
            $('#dialog-session-expiry').attr('style', 'z-index: 999999;');
        }
    }

    private expireSession(): void {
        var self = this;
        self.postJsonData("/api/account/logout", null, function () {
            // Success so continue 
            self.writeSetting("isLoggedIn", "0");
        }, function () {
            // Error so assume authentication issue and request signin 
            self.writeSetting("isLoggedIn", "0");
        });
    }

    public createIngredient(): void {
        if ($(".create-ingredient-button-container2").is(":visible")) {
            $(".create-ingredient-button-container2").fadeOut("fast");
        } else {
            $(".create-ingredient-button-container2").fadeIn("fast");
        }
    }

    public createRegular(): void {
        location.href = "/ingredient/create";
    }

    public createMeat(): void {
        location.href = "/ingredient/createmeatingredient";
    }
	
    public createRecipe(): void {
        this.isBusy(true);
        var action = this.getJsonData("/api/recipes/validateCreate", (message: string) => {
            this.isBusy(false);
            if (message == null) {
                location.href = "/recipe/create";
                return;
            }
            this.showNotification(new ToastNotification(ToastNotificationKind.Info,
                "Trial Subscription",
                message));

        }, (err) => {
            this.isBusy(false);
        });
    }	

    public deepClone<T>(obj: T) : T {
        return JSON.parse(JSON.stringify(obj)); // deep clone of object
    }

    public copyText(text: string) {
        var self = this;

        if (this.getBroswer() == "firefox") {
            navigator.clipboard.writeText(text).then(function () {
                /* clipboard successfully set */
                self.showNotification(new ToastNotification(ToastNotificationKind.Success, "Clipboard", "Content copied to the clipboard OK"));
            }, function () {
                /* clipboard write failed */
                self.showNotification(new ToastNotification(ToastNotificationKind.Error, "Clipboard", "Content failed to be copied to the clipboard"));
            });
        } else {
            // tslint:disable-next-line
            function listener(e: any) {
                e.clipboardData.setData("text/plain", text);
                e.preventDefault();
            }
            document.addEventListener("copy", listener);
            document.execCommand("copy", true);
            document.removeEventListener("copy", listener);
            this.showNotification(new ToastNotification(ToastNotificationKind.Success, "Clipboard", "Content copied to the clipboard OK"));
        }
    }

}

// ReSharper disable once Class
// tslint:disable-next-line
function showValidationError(element: HTMLElement, field: KnockoutObservable<any>) {

    var dialog = $(element).closest("dialog");
    var containerId = (dialog.length > 0) ? "#" + dialog.get(0).id : "main.main-content";
    var adjustY = (dialog.length > 0) ? -20 : 0;
    var adjustX = (dialog.length > 0) ? -4 : 0;

    var tips = $(element).qtip({
        id: "validation-error-tip",
        content: {
            text: field.error()
        },
        position: {
            my: "bottom left",
            at: "top center",
            container: $(containerId),
            viewport: $("main.main-content"),
            adjust: {
                y: adjustY,
                x: adjustX
            }
        },
        show: {
            event: false
        },
        hide: {
            fixed: true,
            event: "unfocus click"
        },
        style: {
            classes: "qtip-light validation-error-tip"
        }
    });

    var api = tips.qtip("api");
    api.toggle(true);
    setTimeout(() => {
        api.destroy(false);
    }, 5000);

}

class SessionexpiryDialogViewModel
    extends DialogViewModel {

    public username = ko.observable<string>();
    public password = ko.observableArray<string>(null);
    public inputCode = ko.observable<string>();
    public showTwoFactorAuth = ko.observable<boolean>(false);
    public isLoading = ko.observable<boolean>(false);
    public showErrorMessage = ko.observable<boolean>(false);

    constructor(public viewModel: ViewModelBase) {
        super("session-expiry",
            "Your session has expired",
            KnockoutExtensions.getTemplateByName("session-expiry-dialog-content"),
            [
                new DialogAction("Sign In", () => {
                    this.login();
                    return true;
                }, true)
            ]);
        this.username(this.viewModel.readSetting("nutricalc-login"));
        this.password.subscribe(() => this.showErrorMessage(false));
        this.inputCode.subscribe(() => this.showErrorMessage(false));
        
    }

    public login() {
        this.showErrorMessage(false);
        this.actions[0].caption("Signing In");
        this.isLoading(true);
        this.disableActions();
        this.viewModel.postJsonData("/api/account/login", { email: this.username(), password: this.password(), inputCode: this.inputCode(), returnUrl: "" }, (response: LoginResponse) => {
            this.actions[0].caption("Sign In");
            this.isLoading(false);
            this.enableActions();
            if (response.isSuccess) {
                this.viewModel.writeSetting("isLoggedIn", "1");
                $('.sessionexpired-overlay').hide();
                this.viewModel.isSessionExpired(false);
                this.close();
            } else if (response.requiresTwoFactor) {
                this.showTwoFactorAuth(true);
                this.setFocus("#authenticatorCode");
            } else {
                this.showErrorMessage(true);
                this.setFocus(this.showTwoFactorAuth() ? "#authenticatorCode" : "#password");
            }
        }, () => {
            this.actions[0].caption("Sign In");
            this.isLoading(false);
            this.enableActions();
            this.showErrorMessage(true);
            this.setFocus(this.showTwoFactorAuth() ? "#authenticatorCode" : "#password");
        });
    }
}

/**
 * Uses the toJs() method and knockout observables to track the dirty state of an object.
 */
class DirtyFlag {
    isDirty: KnockoutComputed<boolean>;
    reset: () => void;
    onChange: () => void;

    // tslint:disable-next-line
    public static createInstance(root: ViewModelBase, isInitiallyDirty: boolean) {
        // tslint:disable-next-line
        var result: any = () => { };

        // ReSharper disable once InconsistentNaming
        var _initialState = ko.observable(JSON.stringify(root.toJs()));
        // ReSharper disable once InconsistentNaming
        var _isInitiallyDirty = ko.observable(isInitiallyDirty);

        result.isDirty = ko.computed<boolean>(() => {
            if (result.onChange) {
                result.onChange();
            }
            return _isInitiallyDirty() || _initialState() !== JSON.stringify(root.toJs());
        });

        result.reset = () => {
            _initialState(JSON.stringify(root.toJs()));
            _isInitiallyDirty(false);
        };

        result.onChange = null;

        return result as DirtyFlag;
    }
}

/**
 * Default global handler for ajax errors.
 */
// tslint:disable-next-line
$((document as any)).ajaxError(
    (event: JQuery.Event, jqhxr: JQueryXHR, settings: JQueryAjaxSettings, thrownError: string) => {
        if (jqhxr.status === 400) {
            // Bad Request
            if (jqhxr.responseJSON != null && Object.prototype.toString.call(jqhxr.responseJSON) != '[object String]') {
                // Json object available - each property on the responseJSON represents a key with an issue
                if (!jqhxr.responseJSON.hasOwnProperty("Name")) {
                    $.each(jqhxr.responseJSON,
                        (index: number, item: string[]) => {
                            const key = index.toString();
                            $.each(item,
                                (messageIndex: number, message: string) => {
                                    const notification = new ToastNotification(ToastNotificationKind.Error,
                                        "Validation Error",
                                        message);
                                    notification.show();
                                });
                        });
                } else {
                    const notification = new ToastNotification(ToastNotificationKind.Error,
                        "Validation Error",
                        jqhxr.responseJSON.Name[0]);
                    notification.show();
                }
            } else if (jqhxr.responseText != null) {
                const notification = new ToastNotification(ToastNotificationKind.Error,
                    "Error",
                    `${jqhxr.responseText.replace(/^"(.*)"$/, "$1")}`);
                notification.show();
            } else {
                var supportUrl = JSON.parse(window.localStorage.getItem('app-settings'))['supportUrl'];
                const notification = new ToastNotification(ToastNotificationKind.Error,
                    "Unexpected Error",
                    `A request to the NutriCalc server failed (${jqhxr.status}: ${thrownError
                    }).<br/><br/>Please retry and <a href='${supportUrl}' target='_blank'>click here for support</a> if the problem persists.`);
                notification.show();
            }
        }
    });

class RecentItemsViewModel extends ViewModelBase {

    private _key: string = null;
    public items = ko.observableArray<RecentItem>();
    private _maxItems = 10;

    public constructor(key: string) {
        super();
        this._key = key;
        this.load();
    }

    private load() {
        // Load from settings
        var json = this.readSetting(`recent-${this._key}`);
        if (json != null) {
            ko.mapping.fromJS(JSON.parse(json), {}, this.items);

            if (this._key == "ingredient" && this.settings.ingredientNamesUppercase) {
                for (var i = 0; i < this.items().length; i++) {
                    this.items()[i].caption(this.items()[i].caption().toLocaleUpperCase());
                }
            }
            if (this._key == "recipe" && this.settings.recipeNamesUppercase) {
                for (var r = 0; r < this.items().length; r++) {
                    this.items()[r].caption(this.items()[r].caption().toLocaleUpperCase());
                }
            }
        }
    }

    public addItem(id: number, caption: string, sourceId: number) {
        this.load();
        var newItems = ko.utils.arrayFilter(this.items(), item => item.id() !== id);
        if (newItems.length > this._maxItems - 1) {
            newItems.splice(this._maxItems - 1);
        }

        var iconName = "history";
        var tooltip = null;
        switch (sourceId) {
            case 0: // ???
                iconName = "account_box";
                break;
            case 1: // Supplimentary
                iconName = "library_add";
                break;
            case 5:
            case 2: // USDA
                iconName = "language";
                tooltip = "USDA Ingredient";

                break;
            case 3: // Main ingredients
                iconName = "library_books";
                break;
            case 10:
                iconName = "folder";
                break;
            case 11: // Sub recipe
                iconName = "folder_open";
                break;
            default:
                iconName = "account_box";
                break;
        }

        newItems.unshift(new RecentItem(id, caption, iconName, tooltip));
        this.items(newItems);

        this.save();
    }

    public removeItemById(id: number) {
        this.load();
        this.items(ko.utils.arrayFilter(this.items(), item => item.id() !== id));
        this.save();
    }

    public save() {
        this.writeSetting(`recent-${this._key}`, ko.toJSON(this.items));

        if (this.isSavingSettings)
            return;

        // Save the data to the database as well
        var userSettings: UserSettingsUpdate = new UserSettingsUpdate();
        if (this.readSetting("lastNotificationId") != null) {
            userSettings.lastNotificationId = parseInt(this.readSetting("lastNotificationId"));
        }
        userSettings.recentIngredients = this.readSetting("recent-ingredient");
        userSettings.recentRecipes = this.readSetting("recent-recipe");

        this.postJsonData("/api/account/savesettings", userSettings,
            () => {
                // No action at this time
                //console.log(`Saved local usage settings for user`);
            },
            (err) => {
                console.log(`Failed to save local user settings. ${err.statusText}`);
            }
        );
    }
}

class RecentItem {
    public id = ko.observable<number>(null);
    public caption = ko.observable<string>(null);
    public iconName = ko.observable<string>(null);
    public tooltip = ko.observable<string>(null);
    constructor(id: number, caption: string, iconName: string, tooltip: string) {
        this.id(id);
        this.caption(caption);
        this.iconName(iconName);
        this.tooltip(tooltip);
    }
}

class RecentItemsContainer {
    private static _recentRecipes: RecentItemsViewModel = new RecentItemsViewModel("recipe");
    private static _recentIngredients: RecentItemsViewModel = new RecentItemsViewModel("ingredient");

    private constructor() { }

    // tslint:disable-next-line
    public static getRecentRecipes(): RecentItemsViewModel {
        return this._recentRecipes || (this._recentRecipes = new RecentItemsViewModel("recipe"));
    }

    public static getRecentIngredients(): RecentItemsViewModel {
        return this._recentIngredients || (this._recentIngredients = new RecentItemsViewModel("ingredient"));
    }

}

class CopyToClipboardOptions {
    targetSelector: () => string | string;
    beforeCopy: () => void;
    success: () => void;
    error: () => void;
    title: () => string | string;
    rightAlignTables: () => boolean | boolean;
}

export { ViewModelBase, RecentItemsViewModel, RecentItem, DirtyFlag, CopyToClipboardOptions, SessionexpiryDialogViewModel };


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./Scripts/ViewModels/viewmodelbase.ts